import AddIcon from '@mui/icons-material/Add';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { CSSProperties, FC } from 'react';

import { AssignableRecipient } from '@apiContract';
import { GroupIcon } from '@components/GroupIcon';
import { sxCutText } from 'src/theme/shared/styles';

import { getRecipientFullName, getRecipientInitials } from '@utils/recipient';

import {
  sxAvatar,
  sxCheckOutIcon,
  sxIconGrey500,
  sxItemAvatar,
  sxItemButton,
  sxLocationBox,
  sxSelectedBox,
} from '../../styles';

type ListItemProps = {
  selected: boolean;
  isUnassign?: boolean;
  style: CSSProperties;
  item: AssignableRecipient | null;
  handleClick: () => void;
  changeTab?: () => void;
  isForward?: boolean;
};

const Item: FC<ListItemProps> = ({ style, selected, item, handleClick, changeTab, isForward }) => {
  const theme = useTheme();
  const isGroup = !!item?.groupId;
  const user = item?.user;

  if (!item && !changeTab) return null;

  const userFullName = getRecipientFullName(item);
  const userInitials = getRecipientInitials(item);

  let secondaryText: string | null | undefined = '';
  if (isForward) {
    secondaryText = isGroup ? '' : user?.userName || '';
  } else if (isGroup) {
    secondaryText = item?.group?.autoAssignId;
  } else {
    secondaryText = `${user?.userName}${(!!user?.userName && !!user?.autoAssignId && ' • ') || ''}${
      user?.autoAssignId || ''
    }`;
  }

  const renderAvatar = () => {
    if (isGroup) {
      return <GroupIcon avatarSx={{ ...sxAvatar, bgcolor: 'grey.700' }} iconSx={sxIconGrey500} />;
    }

    return (
      <Avatar sx={sxAvatar} alt={userFullName} src={user?.profileImageUrl}>
        {user && !user.profileImageUrl ? userInitials : <AddIcon sx={sxIconGrey500} />}
      </Avatar>
    );
  };

  return (
    <ListItem component="div" sx={style} disablePadding>
      <ListItemButton
        onClick={item ? handleClick : changeTab}
        sx={{ ...sxItemButton(selected, theme) }}
        data-testid="rd-assign-to-user"
      >
        <ListItemAvatar sx={sxItemAvatar}>
          {renderAvatar()}
          {selected ? (
            <Box sx={sxSelectedBox}>
              <CheckOutlinedIcon sx={sxCheckOutIcon} />
            </Box>
          ) : null}
        </ListItemAvatar>
        {item ? (
          <ListItemText
            primary={
              <Typography
                title={item.displayName || ''}
                variant="subtitle2"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
              >
                {item.displayName}
              </Typography>
            }
            secondary={
              <Typography
                title={user?.userName}
                color="text.secondary"
                variant="body2"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
              >
                {secondaryText}
              </Typography>
            }
          />
        ) : (
          <Typography variant="subtitle2">Create New User</Typography>
        )}
        {isForward && user?.locationName && (
          <Box sx={sxLocationBox}>
            <Typography
              title={user.locationName}
              color="text.secondary"
              variant="body2"
              textAlign="right"
              sx={sxCutText}
            >
              {user.locationName}
            </Typography>
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
};

export default Item;
